// @ts-check
"use strict"

import * as Sentry from "@sentry/react";
import React, { createContext, useContext, useEffect, useRef, useState } from "react"
import Markdown from "react-markdown"
import remarkGfm from "remark-gfm"
import rehypeRaw from "rehype-raw"
import { escapeSmileMarkdownCharacters } from "../../utils/smiles"
import { showErrorMessage } from "@minohealth/common/utils/notifiers"
import AutoResizingTextarea from "@minohealth/common/components/AutoResizingTextarea"
import { formatTextWithCitation } from "@/utils/citations"
import { TooltipProvider } from "@/components/ui/tooltip"
import ChatBubbleCite from "./ChatBubbleCite"
import ChartRenderer from "./ChartRenderer"
import { splitChartBlocks } from "./parseChartXml"
export { ChatBubbleStreamingContext } from "./ChatBubbleStreamingContext"
import { useLinkPreview, getFallbackData } from "../../services/useLinkPreview"
import { PiLinkBold } from "react-icons/pi";

export const SHOW_SOURCES_EVENT = "show-sources-event"

import { ChatBubbleStreamingContext } from "./ChatBubbleStreamingContext"

import { MdContentCopy } from 'react-icons/md'
import { BsCheck } from 'react-icons/bs'

import "./chat-bubble.css"

/**
 * @typedef {Object} ChatBubbleProps
 * @property {"user" | "ai"} variant
 * @property {boolean=} isSendingMessage
 * @property {boolean=} hasReport
 */

/** @param {React.ComponentProps<"article"> & ChatBubbleProps} props */
export function ChatBubble({ variant, isSendingMessage, hasReport, ...restOfProps }) {
  return (
    <article
      className="chat-bubble"
      data-variant={variant}
      data-has-report={hasReport}
      data-sending={isSendingMessage}
      {...restOfProps}
    />
  )
}

/** @param {React.ComponentProps<"header">} props */
export function ChatBubbleHeader(props) {
  return (
    <header className="chat-bubble__header" {...props} />
  )
}

/** @param {{ timestamp?: string }} props */
export function ChatBubbleTimestamp({ timestamp }) {
  if (timestamp == null) {
    return <p className="chat-bubble__timestamp">Sending...</p>
  }

  return (
    <time
      className="chat-bubble__timestamp"
      dateTime={new Date(timestamp).toISOString()}
    >
      {formatDate(timestamp)}
    </time>
  )
}

/** @param {React.ComponentProps<"ul">} props */
export function ChatBubbleActionList(props) {
  return (
    <ul className="chat-bubble__action-list" {...props} />
  )
}

/**
 * @typedef {Object} ChatBubbleActionItemExtraProps
 * @property {string} title
 * @property {"large" | "medium"} [variant="medium"]
 */

/** @param {React.ComponentProps<"button"> & ChatBubbleActionItemExtraProps} props */
export function ChatBubbleActionItem({ title, variant, children, ...restOfProps }) {
  return (
    <li className="chat-bubble__action-item" data-variant={variant}>
      <button
        type="button"
        title={title}
        {...restOfProps}
      >
        {children}
        <span className="sr-only">{title}</span>
      </button>
    </li>
  )
}

/** @param {React.ComponentProps<"div">} props */
export function ChatBubbleContent(props) {
  return (
    <div className="chat-bubble__content" {...props} />
  )
}

/**
 * 
 * @param {string} text 
 * @param {import("@/utils/citations").Citation[]} citations 
 */
function safeFormatTextWithCitation(text, citations) {
  try {
    return formatTextWithCitation(text, citations)
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        context: 'ChatBubble - format text with citation',
        textLength: text?.length,
        citationsCount: citations?.length
      }
    });
    console.error("Error formatting text with citation", error)
    return text
  }
}

/**
 * @typedef {Object} ChatBubbleTextProps
 * @property {string} messageText
 * @property {boolean=} shouldDisplayMarkdown
 * @property {boolean} isStreaming
*/

/** @param {ChatBubbleTextProps} props */
export function ChatBubbleText({ messageText = "", shouldDisplayMarkdown = true, isStreaming = false }) {
  if (messageText === "") return null

  if (isStreaming && messageText === "") {
    return (
      <div
        className="skeleton"
        style={{ width: "7.5ch", opacity: 0.5 }}
      />
    )
  }

  return (
    <div className="chat-bubble__text-wrapper">
      {!shouldDisplayMarkdown && <p>{messageText}</p>}
      {shouldDisplayMarkdown && (
        <TooltipProvider>
          <ChatMarkdown isStreamingText={isStreaming}>
            {messageText}
          </ChatMarkdown>
        </TooltipProvider>
      )}
    </div>
  )
}

/**
 * @param {Object} props
 * @param {string} props.defaultValue
 * @param {(text: string) => void} props.onSave
 * @param {() => void} props.onCancel
 * @param {string} props.formId
 */
export function ChatBubbleTextInput({ defaultValue, onSave, onCancel, formId }) {
  const [messageText, setMessageText] = useState(defaultValue)
  const isMessageEditted = messageText !== defaultValue

  /** @param {React.FormEvent<HTMLFormElement>} event */
  function handleSubmit(event) {
    event.preventDefault()
    const form = event.target
    if (!(form instanceof HTMLFormElement)) return

    const formData = new FormData(form)
    const text = formData.get(CHAT_BUBBLE_TEXT_INPUT_MESSAGE_FIELD_NAME)
    if (typeof text !== "string") return
    if (text === defaultValue) return
    if (text === "") return

    onSave(text)
  }

  /** @param {React.FormEvent<HTMLFormElement>} event */
  function handleReset(event) {
    event.preventDefault()
    onCancel()
  }

  /** @param {React.KeyboardEvent<HTMLTextAreaElement>} event */
  function handleEnterToSubmit(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      const textArea = event.target;
      if (!(textArea instanceof HTMLTextAreaElement)) return;

      const form = textArea.form
      if (!(form instanceof HTMLFormElement)) return;

      if (!isMessageEditted) return

      form.requestSubmit()
    }
  }

  return (
    <div className="chat-bubble__text-input">
      <form
        id={formId}
        onSubmit={handleSubmit}
        onReset={handleReset}
      >
        <div className="chat-bubble__text-input-form-field">
          <AutoResizingTextarea
            rows={1}
            name={CHAT_BUBBLE_TEXT_INPUT_MESSAGE_FIELD_NAME}
            defaultValue={defaultValue}
            onChange={event => setMessageText(event.target.value)}
            onKeyDown={handleEnterToSubmit}
            autoFocus
            autoComplete="off"
            spellCheck="false"
          />
        </div>
      </form>
    </div>
  )
}
const CHAT_BUBBLE_TEXT_INPUT_MESSAGE_FIELD_NAME = "message-text"

/**
 * @typedef ChatMarkdownBaseExtraProps
 * @property {boolean} [isStreamingText]
 */

/**
 * @type {React.FC<React.HTMLProps<HTMLDivElement> & ChatMarkdownBaseExtraProps & {children: string}>}
 */
export const ChatMarkdownBase = React.memo(
  function ChatMarkdownbase({ isStreamingText = false, children, ...props }) {
    const { citations } = useContext(ChatBubbleMessageCitationsContext)

    // Apply citation formatting only to text segments so chart JSON (which
    // contains number arrays like [27,31,24,10,8]) is never passed through
    // the citation parser, which would treat those arrays as citation groups.
    const processedChildren = splitChartBlocks(children ?? "").map(part =>
      part.type === 'text'
        ? safeFormatTextWithCitation(escapeSmileMarkdownCharacters(part.content), citations)
        : part.type === 'chart' ? part.raw : ''
    ).join("")

    return (
      <ChatBubbleStreamingContext value={isStreamingText}>
        <div {...props}>
          <Markdown
            remarkPlugins={REMARK_PLUGINS}
            rehypePlugins={REHYPE_PLUGINS}
            components={MARKDOWN_COMPONENTS}
          >
            {processedChildren}
          </Markdown>
        </div>
      </ChatBubbleStreamingContext>
    )
  }
)

/**
 * @param {Object} props
 * @param {boolean} [props.isStreamingText]
 * @param {string} props.children
 */
export function ChatMarkdown(props) {
  return (
    <ChatMarkdownBase className="chat-bubble__markdown" {...props} />
  )
}

const REMARK_PLUGINS = [remarkGfm]
const REHYPE_PLUGINS = [rehypeRaw]

/** @satisfies {import("react-markdown").Components} */
const MARKDOWN_COMPONENTS = {
  // @ts-ignore — chart is not a standard HTML element
  chart: ChartRenderer,
  a: ({ node, ...props }) => (
    <a target="_blank" {...props} />
  ),
  pre: ({ node, children, ...props }) => (
    <CodeBlock {...props}>{children}</CodeBlock>
  ),
  table: ({ node, ...props }) => (
    <div className="chat-bubble__table-wrapper">
      <table {...props} />
    </div>
  ),
  cite: ({ node, children, ...props }) => {
    /** @type {*} */
    const anyProps = props
    const citationNumber = Number(anyProps["data-citation-number"])
    const { citations } = useContext(ChatBubbleMessageCitationsContext)

    const citation = citationNumber > 0 ? citations[citationNumber - 1] : null

    if (citationNumber === 0) {
      return <cite {...props}>{children}</cite>;
    }

    return (
      <ChatBubbleCite
        number={citationNumber.toString()}
        url={citation.url}
        citation={citation}
      />
    )
  }
}

/** @param {React.ComponentProps<"pre">} props */
function CodeBlock({ children, ...props }) {
  const [isCodeCopied, setIsCodeCopied] = useState(false)
  const [codeLang, setCodeLang] = useState(null)
  const blockRef = useRef(null)

  useEffect(() => {
    const codeClassName = blockRef.current?.querySelector("code")?.className ?? ""
    const lang = /language-([^\s]+)/.exec(codeClassName)?.[1]
    setCodeLang(lang)
  }, [])

  useEffect(() => {
    if (!isCodeCopied) return

    const timeout = setTimeout(() => {
      setIsCodeCopied(false)
    }, 2000)

    return () => clearTimeout(timeout)
  }, [isCodeCopied])

  function copyCode() {
    const codeText = blockRef.current?.querySelector("code")?.textContent ?? ""

    navigator.clipboard.writeText(codeText)
      .then(() => setIsCodeCopied(true))
      .catch(err => showErrorMessage(err.message))
  }

  return (
    <div className="chat-bubble__code-block">
      {codeLang != null && (
        <p className="chat-bubble__code-block-lang">{codeLang}</p>
      )}

      <pre {...props} ref={blockRef}>
        <button
          type="button"
          title={isCodeCopied ? 'Copied' : 'Copy code'}
          className="chat-bubble__code-block-copy-btn"
          data-copied={isCodeCopied}
          onClick={copyCode}
        >
          {!isCodeCopied
            ? <MdContentCopy />
            : <BsCheck />
          }
          <span className="sr-only">Copy</span>
        </button>

        {children}
      </pre>
    </div>
  )
}

/**
 * Individual citation item component that uses the link preview hook
 * @param {Object} props
 * @param {import("@/utils/citations").Citation} props.citation
 * @param {number} props.index
 */
function CitationItem({ citation, index }) {
  const { data: linkPreviewData, isLoading, error } = useLinkPreview(citation.url);
  const linkPreview = linkPreviewData ?? getFallbackData(citation.url);

  return (
    <a
      key={index}
      href={citation.url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 px-3 py-1.5 bg-muted/50 border border-border/50 rounded-2xl text-muted-foreground no-underline text-sm font-medium transition-all duration-200 ease-in-out backdrop-blur-sm hover:bg-muted hover:border-border hover:-translate-y-0.5 hover:shadow-sm active:translate-y-0"
      title={linkPreview.title}
    >
      <div className="relative w-4 h-4 flex items-center justify-center shrink-0">
        {linkPreview.favicon && (
          <img
            src={linkPreview.favicon}
            alt={`${linkPreview.domain} icon`}
            className="w-4 h-4 rounded-sm object-contain"
          />
        )}
      </div>
      <span className="truncate max-w-[9.375rem]">{linkPreview.domain}</span>
    </a>
  )
}

/**
 * @param {Object} props
 * @param {import("@/utils/citations").Citation[]} props.citations
 */
function SourcesFaviconDeck({ citations }) {
  const displayCitations = citations.slice(0, 4);

  return (
    <div className="flex -space-x-2 mr-2">
      {displayCitations.map((citation, index) => (
        <FaviconCircle key={index} url={citation.url} index={index} />
      ))}
    </div>
  );
}

/**
 * @param {Object} props
 * @param {string} props.url
 * @param {number} props.index
 */
function FaviconCircle({ url, index }) {
  const { data: preview } = useLinkPreview(url);
  const favicon = preview?.favicon || getFallbackData(url).favicon;

  return (
    <div
      className="w-5 h-5 rounded-full border border-background bg-muted flex items-center justify-center overflow-hidden shrink-0 shadow-sm"
      style={{ zIndex: 3 - index }}
    >
      {favicon ? (
        <img src={favicon} alt="" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full bg-muted-foreground/20 rounded-full" />
      )}
    </div>
  );
}

/**
 * @param {Object} props
 * @param {import("@/utils/citations").Citation[]} props.messageCitations
 * @param {boolean} props.isStreamingMessage
 */
export function ChatBubbleWebSources({ messageCitations, isStreamingMessage }) {
  if (isStreamingMessage) return null
  if (messageCitations.length === 0) return null

  function handleShowSources() {
    document.dispatchEvent(new CustomEvent(SHOW_SOURCES_EVENT, {
      detail: { citations: messageCitations }
    }))
  }

  return (
    <div className="flex flex-wrap gap-2 mt-4">
      <button
        type="button"
        onClick={handleShowSources}
        className="flex items-center gap-1.5 px-1.5 py-1.5 backdrop-blur-md bg-muted/40 border border-border/40 rounded-full text-foreground no-underline text-xs font-medium transition-all duration-200 ease-in-out hover:bg-muted/60 hover:border-border/60 hover:-translate-y-0.5"
      >
        <SourcesFaviconDeck citations={messageCitations} />
        <span className="ml-0.5">
          { messageCitations.length}
        </span>
        <span className="opacity-90">Sources</span>
      </button>
    </div>
  )
}

/** @param {React.ComponentProps<"footer">} props */
export function ChatBubbleFooter(props) {
  return (
    <footer className="chat-bubble__footer" {...props} />
  )
}

/** @param {Date | string} str */
function formatDate(str) {
  try {
    const formattedParts = DATE_TIME_FORMAT.formatToParts(new Date(str))
    const formatted = Object.fromEntries(
      formattedParts
        .filter(part => part.type !== "literal")
        .map(part => ([part.type, part.value]))
    )

    const { day, month, hour, minute, dayPeriod } = formatted
    return `${day} ${month} at ${hour}:${minute} ${dayPeriod.toUpperCase()}`
  } catch (err) {
    Sentry.captureException(err, {
      extra: {
        context: 'ChatBubble - format date',
        dateString: str,
        dateType: typeof str
      }
    });
    return DATE_TIME_FORMAT.format(new Date(str))
  }
}

const DATE_TIME_FORMAT = new Intl.DateTimeFormat('en-GB', {
  day: "2-digit",
  month: 'short',
  hour: "numeric",
  minute: "numeric",
  hour12: true
})

export const ChatBubbleMessageCitationsContext = createContext({
  /** @type {import("@/utils/citations").Citation[]} */
  citations: null,
})